/**
 * useChat hook -- manages chat message state, SSE streaming lifecycle,
 * and AbortController cleanup.
 *
 * Uses a messagesRef pattern to avoid including `messages` in the sendMessage
 * useCallback dependency array. This gives stable callback identity while
 * accessing current messages when building the request payload.
 */

import { useState, useCallback, useRef, useEffect } from "react";
import { fetchAuthSession } from "aws-amplify/auth";
import { streamChat } from "../lib/sse-client";

/**
 * A single chat message (user or assistant)
 */
export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  toolCalls?: Array<{
    type: "start" | "end";
    toolName: string;
    result?: string;
  }>;
  createdAt: string;
}

/**
 * Last tool result exposed for sidebar integration
 */
export interface ToolResult {
  toolName: string;
  result: string;
}

/**
 * Return type of the useChat hook
 */
export interface UseChatReturn {
  messages: ChatMessage[];
  isStreaming: boolean;
  error: string | null;
  lastToolResult: ToolResult | null;
  sendMessage: (content: string) => Promise<void>;
  clearChat: () => void;
}

interface UseChatOptions {
  onStreamComplete?: () => void;
}

/**
 * Chat hook that manages the full streaming lifecycle.
 *
 * @param chatUrl - Lambda Function URL for chat streaming
 * @param options - Optional callbacks
 * @returns Chat state and control functions
 */
export function useChat(chatUrl: string, options?: UseChatOptions): UseChatReturn {
  const { onStreamComplete } = options ?? {};
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastToolResult, setLastToolResult] = useState<ToolResult | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const messagesRef = useRef<ChatMessage[]>([]);
  const authTokenRef = useRef<string | null | undefined>(undefined); // undefined = not fetched, null = no token, string = token

  // Keep messagesRef in sync with messages state
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Abort stream on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  // Fetch and cache auth token on mount
  useEffect(() => {
    async function fetchAndCacheToken() {
      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.accessToken?.toString();
        authTokenRef.current = token || null;
      } catch {
        authTokenRef.current = null;
      }
    }
    fetchAndCacheToken();
  }, []);

  // Load persisted messages on mount
  useEffect(() => {
    let cancelled = false;

    async function loadMessages() {
      try {
        // Wait for auth token to be fetched
        while (authTokenRef.current === undefined) {
          await new Promise(resolve => setTimeout(resolve, 10));
        }

        const token = authTokenRef.current;
        if (!token) return;

        const res = await fetch(`${chatUrl}?limit=50`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;

        const data: Array<{
          id: string;
          role: "user" | "assistant";
          content: string;
          createdAt: string;
        }> = await res.json();

        if (cancelled || data.length === 0) return;

        // DB returns newest-first; reverse to chronological order
        const loaded: ChatMessage[] = data.reverse().map((m) => ({
          id: m.id,
          role: m.role,
          content: m.content,
          createdAt: m.createdAt,
        }));

        setMessages(loaded);
      } catch {
        // Silently fail — messages will start fresh
      }
    }

    loadMessages();
    return () => { cancelled = true; };
  }, [chatUrl]);

  const sendMessage = useCallback(
    async (content: string) => {
      // Wait for auth token to be fetched if not yet available
      while (authTokenRef.current === undefined) {
        await new Promise(resolve => setTimeout(resolve, 10));
      }

      const token = authTokenRef.current;

      if (!token) {
        setError("Not authenticated");
        return;
      }

      // Build payload BEFORE state updates — messagesRef is stale after
      // setMessages + await because useEffect updates the ref post-render.
      const payload = [
        ...messagesRef.current.map((m) => ({ role: m.role, content: m.content })),
        { role: "user" as const, content },
      ];

      // Create user message
      const userMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: "user",
        content,
        createdAt: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsStreaming(true);
      setError(null);
      setLastToolResult(null);

      // Create assistant placeholder
      const assistantId = crypto.randomUUID();
      const assistantMessage: ChatMessage = {
        id: assistantId,
        role: "assistant",
        content: "",
        toolCalls: [],
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, assistantMessage]);

      // Abort previous stream, create new controller
      abortRef.current?.abort();
      abortRef.current = new AbortController();

      try {
        await streamChat(
          chatUrl,
          payload,
          token,
          {
            onText: (text) => {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantId
                    ? { ...m, content: m.content + text }
                    : m,
                ),
              );
            },
            onToolStart: (toolName) => {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantId
                    ? {
                        ...m,
                        toolCalls: [
                          ...(m.toolCalls ?? []),
                          { type: "start" as const, toolName },
                        ],
                      }
                    : m,
                ),
              );
            },
            onToolEnd: (toolName, result) => {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantId
                    ? {
                        ...m,
                        toolCalls: (m.toolCalls ?? []).map((tc) =>
                          tc.toolName === toolName && tc.type === "start"
                            ? { ...tc, type: "end" as const, result }
                            : tc,
                        ),
                      }
                    : m,
                ),
              );
              setLastToolResult({ toolName, result });
            },
            onError: (message) => {
              setError(message);
            },
            onDone: () => {
              setIsStreaming(false);
              onStreamComplete?.();
            },
          },
          abortRef.current.signal,
        );
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          setError((err as Error).message);
        }
      } finally {
        setIsStreaming(false);
      }
    },
    [chatUrl, onStreamComplete],
  );

  const clearChat = useCallback(() => {
    setMessages([]);
    setError(null);
    setLastToolResult(null);
  }, []);

  return {
    messages,
    isStreaming,
    error,
    lastToolResult,
    sendMessage,
    clearChat,
  };
}
